import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { useUserStore } from '@/common';
import { useReferAndEarnStore } from '@/common/components/Forex/components/refer-and-earn/store';
import { axios } from '@/library/axios';
import { cookiesStorage } from '@/library/storage';
import useMoengageStore from '@/store/moengage';
import type { BaseResponse, CustomerDetail } from '@/types';
import { authConstants, XSRFHeader } from '@/utils/constants/auth';

const getUserDetails = async (
  userId?: string
): Promise<CustomerDetail | null> => {
  if (!userId) {
    return null;
  }

  const response = await axios<
    CustomerDetail,
    BaseResponse<CustomerDetail>,
    void
  >({ method: 'get', url: `/customers/${userId}` });

  return response.data.data;
};

const getExpiry = () => {
  const expiry = cookiesStorage.getCookie(authConstants.OM_AUTH_USER_EXPIRY);
  if (expiry) return parseInt(expiry, 10);
  return undefined;
};

export const useAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userDetails, setUserDetails] = useState<CustomerDetail | null>(null);
  const [customerId, setCustomerId] = useState<string>('');

  useEffect(() => {
    if (userDetails) {
      setCustomerId(userDetails.id);
    } else {
      setCustomerId('');
    }
  }, [userDetails]);

  const router = useRouter();

  const { setMobileNumber } = useMoengageStore((state) => {
    return {
      setMobileNumber: state.setMobileNumber,
    };
  });

  const resetUserStore = useUserStore((state) => state.reset);

  const userId = cookiesStorage.getCookie(authConstants.OM_AUTH_USER_ID);
  const {
    data,
    isLoading,
    isSuccess,
    refetch: refetchAuth,
  } = useQuery(['userDetails', userId], () => getUserDetails(userId), {
    enabled: !!userId,
    useErrorBoundary: false,
    cacheTime: getExpiry(),
    staleTime: getExpiry(),
  });

  useEffect(() => {
    if (userId) {
      setIsLoggedIn(true);
      refetchAuth();
    } else {
      setIsLoggedIn(false);
      cookiesStorage.clearCookie(authConstants.OM_AUTH_USER_ID);
      setUserDetails(null);
    }
  }, [userId, router]);

  useEffect(() => {
    if (data) {
      setUserDetails(data);
      setMobileNumber(data.homePhone || data.mobilePhone || data.businessPhone);
    } else {
      setUserDetails(null);
    }
  }, [data]);

  const logout = () => {
    axios.post('/logout').then(() => {
      resetUserStore();
      useReferAndEarnStore.getState().clear();
      setIsLoggedIn(false);
      setUserDetails(null);
      cookiesStorage.clearCookie(authConstants.OM_AUTH_USER_ID);
      cookiesStorage.clearCookie(authConstants.OM_AUTH_USER_ID);
      cookiesStorage.clearCookie(XSRFHeader);
      // @ts-ignore
      Moengage.destroy_session();
      router.push('/');
    });
  };

  return {
    isLoggedIn,
    userDetails,
    data,
    isLoading,
    isSuccess,
    logout,
    refetchAuth,
    userId: customerId,
  };
};
